import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Square, Upload, Send, Loader2, Star, CheckCircle2 } from 'lucide-react';
import { Section, Eyebrow, H2, FONT } from './ui';

const API = process.env.REACT_APP_BACKEND_URL;
const T = {
  it: { eyebrow: 'Consegna il compito', title: 'Il tuo minuto, analizzato', analysis: 'La tua analisi (circa 100 parole): tratti segmentali, prosodici, identità e pubblico', video: 'Link al video che hai analizzato (YouTube, sito ufficiale…)',
    rec: 'Registra la tua lettura', stop: 'Ferma', upload: 'Oppure carica un file audio', send: 'Consegna', resend: 'Aggiorna la consegna', words: 'parole', saved: 'Consegnato il', needAudio: 'Registra o carica la tua lettura', grade: 'Voto', feedback: 'Il commento di Steve', yours: 'La tua registrazione', err: 'Consegna non riuscita' },
  en: { eyebrow: 'Hand in your assignment', title: 'Your minute, analysed', analysis: 'Your analysis (about 100 words): segmental and prosodic features, identity and audience', video: 'Link to the video you analysed (YouTube, official site…)',
    rec: 'Record your reading', stop: 'Stop', upload: 'Or upload an audio file', send: 'Hand in', resend: 'Update submission', words: 'words', saved: 'Handed in on', needAudio: 'Record or upload your reading', grade: 'Grade', feedback: "Steve's feedback", yours: 'Your recording', err: 'Submission failed' },
};
const msg = (d, fb) => (typeof d === 'string' ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(' · ') : fb);

export const HomeworkPanel = ({ slug, token, lang, status, onChange }) => {
  const t = T[lang] || T.it;
  const hw = status?.homework;
  const [analysis, setAnalysis] = useState(hw?.analysis || '');
  const [video, setVideo] = useState(hw?.video_url || '');
  const [blob, setBlob] = useState(null);
  const [rec, setRec] = useState(null);
  const [mine, setMine] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const chunks = useRef([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!hw?.audio_mime) return;
    axios.get(`${API}/api/lectures/${slug}/homework/audio`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }).then((r) => setMine(URL.createObjectURL(r.data))).catch(() => {});
  }, [hw?.submitted_at, hw?.audio_mime, slug, token]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream); chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((x) => x.stop());
        const type = (r.mimeType || 'audio/webm').split(';')[0];
        setBlob(new File(chunks.current, `lettura.${type.includes('mp4') ? 'm4a' : 'webm'}`, { type }));
      };
      r.start(); setRec(r);
    } catch { setErr(lang === 'en' ? 'Microphone not available' : 'Microfono non disponibile'); }
  };
  const submit = async () => {
    if (!blob && !hw?.audio_mime) { setErr(t.needAudio); return; }
    setBusy(true); setErr('');
    const fd = new FormData(); fd.append('analysis', analysis); fd.append('video_url', video);
    if (blob) fd.append('audio', blob, blob.name || 'lettura.webm');
    try { await axios.post(`${API}/api/lectures/${slug}/homework`, fd, auth); setBlob(null); onChange(); }
    catch (e) { setErr(msg(e.response?.data?.detail, t.err)); } finally { setBusy(false); }
  };
  // One URL per recording: a new URL on every render would reset the player.
  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : ''), [blob]);
  useEffect(() => () => blobUrl && URL.revokeObjectURL(blobUrl), [blobUrl]);
  const words = analysis.trim() ? analysis.trim().split(/\s+/).length : 0;
  const reviewed = !!hw?.review;

  return (
    <Section id="compito" className="bg-gradient-to-b from-blue-50 to-white border-t border-blue-100 pb-24 sm:pb-0">
      <Eyebrow>{t.eyebrow}</Eyebrow><H2 className="mt-4">{t.title}</H2>
      {reviewed && (
        <div className="mt-8 rounded-3xl bg-[#060d24] text-white p-7" data-testid="homework-review">
          <p className="flex items-center gap-2 text-sm text-blue-200"><CheckCircle2 className="w-4 h-4" /> {t.feedback}</p>
          <p className="mt-3 flex items-center gap-1" data-testid="homework-grade">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-6 h-6 ${n <= hw.review.grade ? 'fill-amber-400 text-amber-400' : 'text-white/20'}`} />)} <span className="ml-2 font-bold">{hw.review.grade}/5</span></p>
          <p className="mt-3 whitespace-pre-line text-blue-50">{hw.review.comment}</p>
        </div>
      )}
      <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="homework-panel">
        <div className="space-y-3">
          <textarea value={analysis} onChange={(e) => setAnalysis(e.target.value)} disabled={reviewed} rows={9} placeholder={t.analysis} className="w-full rounded-2xl border border-slate-300 p-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="homework-analysis" />
          <p className={`text-xs ${words >= 80 && words <= 130 ? 'text-emerald-600' : 'text-slate-500'}`} data-testid="homework-words">{words} {t.words}</p>
          <input value={video} onChange={(e) => setVideo(e.target.value)} disabled={reviewed} placeholder={t.video} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm" data-testid="homework-video" />
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4">
          {!reviewed && (
            <>
              <button onClick={rec ? () => { rec.stop(); setRec(null); } : startRec} className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold ${rec ? 'bg-rose-500 text-white' : 'bg-blue-600 text-white'}`} data-testid="homework-record">
                {rec ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />} {rec ? t.stop : t.rec}
              </button>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Upload className="w-4 h-4" /> {t.upload}
                <input type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files?.[0] && setBlob(e.target.files[0])} data-testid="homework-file" />
              </label>
            </>
          )}
          {blob && <audio controls src={blobUrl} className="w-full" data-testid="homework-new-audio" />}
          {!blob && mine && <div><p className="text-xs text-slate-500 mb-1">{t.yours}</p><audio controls src={mine} className="w-full" data-testid="homework-saved-audio" /></div>}
          {hw?.submitted_at && <p className="text-xs text-emerald-700 font-semibold" data-testid="homework-saved">{t.saved} {new Date(hw.submitted_at).toLocaleString(lang === 'en' ? 'en-GB' : 'it-IT')}</p>}
          {err && <p className="text-sm text-rose-600" data-testid="homework-error">{err}</p>}
          {!reviewed && (
            <button onClick={submit} disabled={busy || words < 10 || !video.trim()} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#060d24] text-white font-bold disabled:opacity-40" data-testid="homework-submit" style={FONT}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {hw ? t.resend : t.send}
            </button>
          )}
        </div>
      </div>
    </Section>
  );
};
